/**
 * Score slices and position policy for a bot.
 *
 * Replaces the two-bot split: one bot covering the whole score range, with a
 * rule per band. Everything a slice does NOT carry — symbol, account, size,
 * TP, blackout, risk limits — stays on the bot, which is the point. Two bots
 * that differed only in band and entry style duplicated all of it.
 *
 * Validation mirrors the engine's (`slices.rs`) and blocks the save, because a
 * silent overlap is how a 60-score signal quietly starts using the pullback
 * rule. The engine re-validates regardless; this just moves the discovery from
 * live trading to the editor.
 */

import React from 'react';
import type { PositionPolicy, ScoreSlice, SliceEntry } from '@shared/types/autoBot';
import { autoLabel, validateSlices } from '../utils/sliceValidation';
import './SliceEditor.css';

interface SliceEditorProps {
  slices: ScoreSlice[];
  positionPolicy: PositionPolicy;
  onChange: (slices: ScoreSlice[], positionPolicy: PositionPolicy) => void;
}

const DEFAULT_PULLBACK: SliceEntry = { kind: 'pullback', offsetPoints: 4, expirySeconds: 360 };

/** Sensible when adding the first slice: the whole range, paying the market. */
const newSlice = (minScore: number): ScoreSlice => ({
  id: `slice_${minScore}_${Math.random().toString(36).slice(2, 8)}`,
  label: autoLabel(minScore, null),
  minScore,
  maxScore: null,
  entry: { kind: 'chase' },
  enabled: true,
});

export const SliceEditor: React.FC<SliceEditorProps> = ({
  slices,
  positionPolicy,
  onChange,
}) => {
  /**
   * In-progress text for numeric fields, keyed `${sliceId}:${field}`.
   *
   * A controlled number input that does `parseFloat(v) || 0` cannot be
   * cleared: backspacing to empty yields NaN, falls back to 0, and the field
   * snaps to "0" so you can only append digits after it. That is how a bound
   * meant to be 55 became 5455.
   *
   * The draft holds exactly what was typed — including "" and "-" — while the
   * model takes a value only once the text parses. Cleared on blur so the
   * field re-syncs to whatever the model actually holds.
   */
  const [drafts, setDrafts] = React.useState<Record<string, string>>({});

  const draftKey = (id: string, field: string) => `${id}:${field}`;

  const numValue = (id: string, field: string, actual: number | null) => {
    const d = drafts[draftKey(id, field)];
    if (d !== undefined) return d;
    return actual === null ? '' : String(actual);
  };

  const clearDraft = (id: string, field: string) =>
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[draftKey(id, field)];
      return next;
    });

  const setDraft = (id: string, field: string, text: string) =>
    setDrafts((prev) => ({ ...prev, [draftKey(id, field)]: text }));
  const issues = validateSlices(slices);
  const badIds = new Set(issues.flatMap((i) => i.ids));

  const update = (id: string, patch: Partial<ScoreSlice>) =>
    onChange(
      slices.map((s) => {
        if (s.id !== id) return s;
        const next = { ...s, ...patch };
        // Keep the label in step with the band unless it was hand-edited, so
        // the common case needs no typing and a custom name still survives.
        if (('minScore' in patch || 'maxScore' in patch) && s.label === autoLabel(s.minScore, s.maxScore)) {
          next.label = autoLabel(next.minScore, next.maxScore);
        }
        return next;
      }),
      positionPolicy
    );

  const setEntry = (id: string, kind: SliceEntry['kind']) =>
    update(id, { entry: kind === 'chase' ? { kind: 'chase' } : { ...DEFAULT_PULLBACK } });

  const addSlice = () => {
    // Start above the highest existing floor so the new row is not born
    // overlapping everything.
    const highest = slices.reduce((m, s) => Math.max(m, s.minScore), 0);
    onChange([...slices, newSlice(Math.min(highest + 25, 95))], positionPolicy);
  };

  const removeSlice = (id: string) =>
    onChange(slices.filter((s) => s.id !== id), positionPolicy);

  const setPolicy = (kind: PositionPolicy['kind']) =>
    onChange(
      slices,
      kind === 'martingale'
        ? { kind, maxMultiplier: positionPolicy.kind === 'martingale' ? positionPolicy.maxMultiplier : 8 }
        : { kind }
    );

  return (
    <div className="slice-editor">
      <div className="slice-head">
        <span className="slice-title">SCORE SLICES</span>
        <button type="button" className="slice-add" onClick={addSlice}>
          + slice
        </button>
      </div>

      {slices.length === 0 ? (
        <div className="slice-empty">
          No slices — this bot uses its confidence band and entry settings instead.
          Adding one switches it to per-band rules.
        </div>
      ) : (
        <div className="slice-rows">
          {slices.map((s) => (
            <div key={s.id} className={`slice-row ${badIds.has(s.id) ? 'invalid' : ''}`}>
              {/* Three groups rather than one flat row. A flat flex row has no
                  break points, so on a narrow screen every child shrinks
                  together until the number fields are unreadable slivers —
                  which is how a mistyped bound slipped in unnoticed. */}
              <div className="slice-group slice-group-name">
              <input
                type="checkbox"
                checked={s.enabled}
                onChange={(e) => update(s.id, { enabled: e.target.checked })}
                title="A disabled slice trades nothing and is exempt from gap checks"
              />
              <input
                className="slice-label"
                value={s.label}
                onChange={(e) => update(s.id, { label: e.target.value })}
                title="Shown on execution records so a fill traces back to its rule"
              />
              </div>
              <div className="slice-group slice-group-band">
              <input
                className="slice-num"
                type="number"
                inputMode="decimal"
                value={numValue(s.id, 'min', s.minScore)}
                onChange={(e) => {
                  setDraft(s.id, 'min', e.target.value);
                  const v = parseFloat(e.target.value);
                  if (Number.isFinite(v)) update(s.id, { minScore: v });
                }}
                onBlur={() => clearDraft(s.id, 'min')}
                title="Inclusive lower bound"
              />
              <span className="slice-dash">to</span>
              <input
                className="slice-num"
                type="number"
                // Empty means no ceiling, which is a real setting rather than a
                // missing one — hence the placeholder saying so.
                inputMode="decimal"
                value={numValue(s.id, 'max', s.maxScore)}
                placeholder="∞"
                onChange={(e) => {
                  setDraft(s.id, 'max', e.target.value);
                  const t = e.target.value.trim();
                  if (t === '') update(s.id, { maxScore: null });
                  else {
                    const v = parseFloat(t);
                    if (Number.isFinite(v)) update(s.id, { maxScore: v });
                  }
                }}
                onBlur={() => clearDraft(s.id, 'max')}
                title="EXCLUSIVE upper bound. Leave empty for no ceiling."
              />
              </div>
              <div className="slice-group slice-group-entry">
              <select
                className="slice-entry"
                value={s.entry.kind}
                onChange={(e) => setEntry(s.id, e.target.value as SliceEntry['kind'])}
              >
                <option value="chase">chase</option>
                <option value="pullback">pullback</option>
              </select>
              {s.entry.kind === 'pullback' && (
                <>
                  <input
                    className="slice-num"
                    type="number"
                    step="0.25"
                    inputMode="decimal"
                    value={numValue(s.id, 'offset', s.entry.offsetPoints)}
                    onChange={(e) => {
                      setDraft(s.id, 'offset', e.target.value);
                      const v = parseFloat(e.target.value);
                      if (Number.isFinite(v)) {
                        update(s.id, {
                          entry: { ...s.entry, offsetPoints: v } as SliceEntry,
                        });
                      }
                    }}
                    onBlur={() => clearDraft(s.id, 'offset')}
                    title="Points BETTER than the signal price to rest the entry"
                  />
                  <span className="slice-unit">pt</span>
                  <input
                    className="slice-num"
                    type="number"
                    inputMode="numeric"
                    value={numValue(s.id, 'expiry', s.entry.expirySeconds)}
                    onChange={(e) => {
                      setDraft(s.id, 'expiry', e.target.value);
                      const v = parseInt(e.target.value, 10);
                      if (Number.isFinite(v)) {
                        update(s.id, {
                          entry: { ...s.entry, expirySeconds: v } as SliceEntry,
                        });
                      }
                    }}
                    onBlur={() => clearDraft(s.id, 'expiry')}
                    title="Cancel the resting entry after this many seconds"
                  />
                  <span className="slice-unit">s</span>
                </>
              )}
              <button
                type="button"
                className="slice-remove"
                onClick={() => removeSlice(s.id)}
                title="Remove this slice"
              >
                ×
              </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {issues.length > 0 && (
        <ul className="slice-issues">
          {issues.map((i, n) => (
            <li key={n}>{i.message}</li>
          ))}
        </ul>
      )}

      <div className="slice-policy">
        <span className="slice-title">WHEN ALREADY POSITIONED</span>
        <div className="slice-policy-row">
          {(['skip', 'flip', 'martingale'] as const).map((k) => (
            <button
              key={k}
              type="button"
              className={`slice-policy-btn ${positionPolicy.kind === k ? 'on' : ''}`}
              onClick={() => setPolicy(k)}
              title={
                k === 'skip'
                  ? 'Sit the signal out'
                  : k === 'flip'
                    ? 'Close and reverse at the same size on an opposing signal'
                    : 'Reverse, doubling after each losing close and resetting on a win'
              }
            >
              {k}
            </button>
          ))}
          {positionPolicy.kind === 'martingale' && (
            <label className="slice-cap">
              cap
              <input
                className="slice-num"
                type="number"
                min={1}
                inputMode="numeric"
                value={numValue('policy', 'cap', positionPolicy.maxMultiplier)}
                onChange={(e) => {
                  setDraft('policy', 'cap', e.target.value);
                  const v = parseInt(e.target.value, 10);
                  if (Number.isFinite(v) && v >= 1) {
                    onChange(slices, { kind: 'martingale', maxMultiplier: v });
                  }
                }}
                onBlur={() => clearDraft('policy', 'cap')}
                title="Size stays at this multiple until a win resets it"
              />
              ×
            </label>
          )}
        </div>
        {positionPolicy.kind === 'martingale' && (
          // Not a tooltip. Base 2 doubled twice is 8 contracts, and it reached
          // that in a single day off two consecutive losses.
          <div className="slice-warn">
            Size doubles after every loss, up to {positionPolicy.maxMultiplier}× base, and stays
            there until a win. At base 2 that is {2 * positionPolicy.maxMultiplier} contracts.
          </div>
        )}
      </div>
    </div>
  );
};

export default SliceEditor;
